import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useTimeSlotSettingsQuery,
  useSaveTimeSlotSettingsMutation,
} from "@queries/time_slot_settings";
import "@styles/opencart-admin.css";

const WEEK_DAYS = [
  { value: "", label: "Not week day" },
  { value: "All", label: "All Days" },
  { value: "Sat", label: "Saturday" },
  { value: "Sun", label: "Sunday" },
  { value: "Mon", label: "Monday" },
  { value: "Tue", label: "Tuesday" },
  { value: "Wed", label: "Wednesday" },
  { value: "Thu", label: "Thursday" },
  { value: "Fri", label: "Friday" },
];

const ORDER_TYPES = [
  "",
  "car_cleaning",
  "normal",
  "skip",
  "express",
  "complementary",
  "disinfection",
  "furniture",
  "housekeeping",
  "carpets",
  "chemicals",
];

export default function TimeSlotSettingsPage() {
  const today = React.useMemo(
    () => new Date().toISOString().slice(0, 10),
    []
  );

  const [filters, setFilters] = React.useState({
    date: today,
    week_day: "",
    order_type: "",
    enable: "1",
  });

  const [appliedFilters, setAppliedFilters] = React.useState({
    date: today,
    week_day: "",
    order_type: "",
    enable: "1",
  });

  const [rows, setRows] = React.useState([]);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } =
    useTimeSlotSettingsQuery(appliedFilters);

  const saveMutation = useSaveTimeSlotSettingsMutation();

  React.useEffect(() => {
    if (data && Array.isArray(data.data)) {
      const normalized = data.data.map((r) => ({
        ...r,
        open: typeof r.open === "number" ? r.open : r.open ? 1 : 0,
      }));
      setRows(normalized);
    }
  }, [data]);

  const total = data?.meta?.total ?? rows.length;

  function handleFilterChange(field, value) {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "date" && value) {
        next.week_day = "";
      } else if (field === "week_day" && value) {
        next.date = "";
      }
      // Automatically apply filters for these fields
      if (["date", "week_day", "order_type", "enable"].includes(field)) {
        setAppliedFilters(next);
      }
      return next;
    });
  }

  function applyFilters(e) {
    if (e) e.preventDefault();
    setAppliedFilters(filters);
  }

  function setRowOpen(timeSlotId, openValue) {
    setRows((prev) =>
      prev.map((row) =>
        row.time_slot_id === timeSlotId ? { ...row, open: openValue } : row
      )
    );
  }

  function setAllOpen(openValue) {
    setRows((prev) => prev.map((row) => ({ ...row, open: openValue })));
  }

  async function handleSave() {
    if (!rows.length) return;

    const payload = {
      date: filters.date || null,
      week_day: filters.week_day || null,
      order_type: filters.order_type || null,
      slots: rows.map((row) => ({
        time_slot_id: row.time_slot_id,
        open: row.open ? 1 : 0,
      })),
    };

    await saveMutation.mutateAsync(payload);
    queryClient.invalidateQueries({ queryKey: ["time-slot-settings"] });
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching || saveMutation.isLoading) && <Loader />}

      <div className="oc-header">
        <h1>Time Slot Settings</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Time Slot Settings</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 16 }}>
          <div>
            <strong>Select week Day and order type</strong>
          </div>
        </div>

        <form onSubmit={applyFilters}>
          <div className="form-row">
            <div className="form-group" style={{ minWidth: 260 }}>
              <label>Time Slot Settings / Select Day</label>
              <input
                type="date"
                className="oc-input"
                value={filters.date}
                onChange={(e) => handleFilterChange("date", e.target.value)}
              />
            </div>

            <div className="form-group" style={{ minWidth: 220 }}>
              <label>Select Day of the week</label>
              <select
                className="oc-input"
                value={filters.week_day}
                onChange={(e) =>
                  handleFilterChange("week_day", e.target.value)
                }
              >
                {WEEK_DAYS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ minWidth: 220 }}>
              <label>Order Type</label>
              <select
                className="oc-input"
                value={filters.order_type}
                onChange={(e) =>
                  handleFilterChange("order_type", e.target.value)
                }
              >
                {ORDER_TYPES.map((ot) => (
                  <option key={ot} value={ot}>
                    {ot === "" ? "" : ot}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group" style={{ minWidth: 180 }}>
              <label>Filter Status</label>
              <select
                className="oc-input"
                value={filters.enable}
                onChange={(e) =>
                  handleFilterChange("enable", e.target.value)
                }
              >
                <option value="1">Enabled</option>
                <option value="all">All</option>
              </select>
            </div>

            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 8,
              }}
            >
              <button type="submit" className="oc-btn ghost">
                Filter
              </button>
              <button
                type="button"
                className="oc-btn primary"
                onClick={handleSave}
                disabled={saveMutation.isLoading || rows.length === 0}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong>Time slots List</strong>
            <button
              type="button"
              className="oc-btn ghost"
              onClick={() => setAllOpen(0)}
            >
              close day
            </button>
            <button
              type="button"
              className="oc-btn ghost"
              onClick={() => setAllOpen(1)}
            >
              Open day
            </button>
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        <table className="oc-table">
          <thead>
            <tr>
              <th style={{ width: 160 }}>close day / Open day</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Presentation</th>
              <th>Type</th>
              <th>Capacity</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                  No time slots
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.time_slot_id}>
                <td>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span>
                      <input
                        type="radio"
                        name={`open-${row.time_slot_id}`}
                        checked={!row.open}
                        onChange={() => setRowOpen(row.time_slot_id, 0)}
                      />{" "}
                      close
                    </span>
                    <span>
                      <input
                        type="radio"
                        name={`open-${row.time_slot_id}`}
                        checked={!!row.open}
                        onChange={() => setRowOpen(row.time_slot_id, 1)}
                      />{" "}
                      open
                    </span>
                  </div>
                </td>
                <td>{row.start_time}</td>
                <td>{row.end_time}</td>
                <td>{row.presentation}</td>
                <td>{row.type}</td>
                <td>{row.capacity}</td>
                <td>{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          style={{
            marginTop: 8,
            textAlign: "right",
            fontSize: 12,
            color: "#666",
          }}
        >
          Showing {rows.length ? 1 : 0} to {rows.length} of {total} (1 Pages)
        </div>
      </div>
    </div>
  );
}
